import type { PlatformContract } from "@ondit/publishing";
import { post, request } from "~/core/api";
import type { PageVariant } from "~/core/api/index";

export type { PageVariant };
export interface Channel {
	platform: string;
	credential_id: string | null;
	target_url: string;
	fields: Record<string, string>;
}
/** A brand's distribution automation: what the optimization workflows do with a page once it is live. */
export interface Policy {
	enabled: boolean;
	mode: "review" | "automatic";
	weekly_credits: number;
	max_pending: number;
	channels: Channel[];
	/** A post gets a generated picture when this names an image model. */
	image_model: string;
	preset: string;
}
export interface PolicyRow {
	weekly_spent: number;
	held: number;
	revision: number;
	config: Policy;
}
export interface Work {
	allowed: boolean;
	id: string;
	page_id: string;
	page_revision: number;
	policy_revision: number;
	config: Policy;
	run_id: string;
	current_revision: number;
	/** Where the promoted page lives, for the source line a post carries. */
	page: { kind: string; slug: string; locale: string };
	status: string;
	reason: string | null;
	/** The published page the posts promote. */
	title: string;
	spent: number;
	held: number;
	created_at: string;
	outcome: string;
	evidence: string;
	/** The page revision's platform versions: the posts. */
	variants: PageVariant[];
}
export interface Overview {
	policy: PolicyRow | null;
	work: Work[];
	decisions: { id: number; title: string; reason: string; created_at: string }[];
	defaults: Policy;
	platforms?: PlatformContract[];
	credentials?: { id: string; provider_id: string; name: string; account: string | null }[];
}
export const marketingKey = (brand: string) => ["marketing", brand];
const base = (brand: string) => `/agent/distribution/${brand}`;
export const overview = (brand: string) => request<Overview>(`${base(brand)}/automation`);
export const savePolicy = (brand: string, revision: number, config: Policy) =>
	post<{ policy: PolicyRow }>(`${base(brand)}/automation`, { expected_revision: revision, config });
export const pausePolicy = (brand: string, revision: number) =>
	post(`${base(brand)}/automation`, { expected_revision: revision, action: "pause" });
export const workCommand = (brand: string, work: string, body: unknown) => post(`${base(brand)}/work/${work}`, body);
export function workState(work: Work): string {
	if (work.status === "cancelled") return "cancelled";
	if (work.variants.some((v) => v.state === "unknown")) return "unknown";
	if (
		work.variants.length === work.config.channels.length &&
		work.variants.length > 0 &&
		work.variants.every((v) => v.state === "published")
	)
		return "published";
	if (work.variants.some((v) => v.state === "failed")) return "failed";
	if (work.variants.some((v) => v.state === "sending")) return "sending";
	if (work.variants.some((v) => v.state === "remote_draft")) return "remote_draft";
	return work.status;
}
